import { Backdrop, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogContentText, Grid } from "@material-ui/core";
import { Add as AddIcon, ArrowBack, Check as CheckIcon, Save as SaveIcon } from "@material-ui/icons";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import SelectSupplier from "../../components/Form/SelectSupplier";
import TextField from "../../components/Form/TextField";
import ButtonUI from "../../components/ui/ButtonUI";
import PageTitle from "../../components/ui/PageTitleUI";
import { useAuth } from "../../hooks/auth";
import MvmStatus from "../../interfaces/MvmStatus";
import { ShippingRequestPost, ShippingRequestPut } from "../../interfaces/ShippingRequest";
import ShippingRequestApi from "../../services/ShippingRequestApi";
import utilsDate from "../../utils/Date";
import { DialogTitle, PaperComponent, useStyles } from "../../utils/DefaultStyles";
import permissions from "../../utils/Permissions";
import PartNumberList from "./PartNumberList";
import { Alert } from "@mui/material";

interface IFormInput {
    id: number;
    supplier: any;
    partNumbers: any;
    plantId: number;
    companyId: number;
    requestDate: any;
    supplierId: number;
    supplierCorporateName: string;
    supplierObservation: string;
    programmerObservation: string;
    partDumpHeaderId: number;
    status: string;
    statusDescription: string;
}

const Submit: React.FC = () => {
    const classes = useStyles();
    const { user, hasPermission } = useAuth();
    const history = useHistory();
    const { t: ts } = useTranslation("ShippingRequest");
    const { t: tGeneral } = useTranslation("General");
    const { t: tValidation } = useTranslation("Validation");
    const [openedPainel, setOpenedPainel] = React.useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [circularLoading, setCircularLoading] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [isCreate, setIsCreate] = useState(false);
    const [isAllowed, setIsAllowed] = useState(true);
    const [isValidate, setIsValidate] = useState(false);
    const [isApproved, setIsApproved] = useState(false);
    const [shippingRequest, setShippingRequest] = useState<any>({});
    const [rejectedWarning, setRejectedWarning] = useState(false);
    const [title, setTitle] = useState<string>("");
    const [supplier, setSupplier] = useState<any>({ code: "", label: "" });
    const [partDumpHeaderId, setPartDumpHeaderId] = React.useState(0);

    const { control, getValues, setError, clearErrors, setValue } =
        useForm<IFormInput>({
            defaultValues: {
                supplier:  { code: '', label: '' },
                requestDate: utilsDate.DateShort(new Date()),
                status: ts(MvmStatus.FLAG_STATUS_DRAFT)
            },
        });

       
    useEffect(() => {
        let contextURL = window.location.pathname.split('/')[2];
        const id = Number(window.location.pathname.replace(`/ShippingRequest/${contextURL}/`, ""));
        let context = contextURL.toLocaleLowerCase();
        
        if (isNaN(id)) {
            setIsAllowed(hasPermission(permissions.SHIPPING_REQUEST.Add))

            setIsEdit(false);
            setIsCreate(true);
            setTitle(ts("createTitle"))

            return;
        }

        switch (context) {
            case "validate":
                setIsAllowed(hasPermission(permissions.SHIPPING_REQUEST.Validate))
                setIsValidate(true);
                break;
            case "duplicate":
                setIsAllowed(hasPermission(permissions.SHIPPING_REQUEST.Add))
                break;
            case "submit":
                setIsAllowed(hasPermission(permissions.SHIPPING_REQUEST.Edit))
                setIsEdit(true);
                context = "edit";
                break;                
            default:
                break;
        }

        setCircularLoading(true);
                
        ShippingRequestApi.get(id)
        .then((response) => {
            if (response) {
                setTitle(`${ts(context + "Title")}`)
                
                setShippingRequest(response);
                response.partNumbers.forEach((item: any) => item.registeredId = item.id);
                setValue("partNumbers", response.partNumbers);
                setValue("supplierCorporateName", response.supplierId + " -  " + response.supplierCorporateName);
                setValue("supplierObservation", response.supplierObservation === null ? "" : response.supplierObservation);
                setValue("status", response.status);
                setValue("supplier", {code: response.supplierId.toString(), label: response.supplierCorporateName})
                setValue("statusDescription", response.statusDescription);
                setValue("requestDate", utilsDate.DateShort(new Date(response.requestDate)));
                if (context === "validate")
                    setValue("programmerObservation", response.programmerObservation)
                
                setCircularLoading(false);
                if (context === "edit" && (!(response.status === "RAS" || response.status === "PRV") || !hasPermission(permissions.SHIPPING_REQUEST.Edit)))
                    setIsAllowed(false);

                // Exibir warning na tela do motivo pelo qual foi rejeitado.. caso tenha sido:
                setRejectedWarning(response.status === "PRV");
                    
            } else {
                setCircularLoading(false);
                enqueueSnackbar(tGeneral("objectNotFound"), {
                    variant: "warning",
                });
            }
        })
        .finally(() => {
            setCircularLoading(false);
        });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [window.location.pathname, user, history]);

    useEffect(() => {
        if (supplier === null || supplier?.code === "") return;

        let contextURL = window.location.pathname.split('/')[2];
        let id = Number(window.location.pathname.replace(`/ShippingRequest/${contextURL}/`, ""));
        
        setCircularLoading(true);

        ShippingRequestApi.searchSchedule(
            isNaN(id) ? 0 : id,
            Number(user.company.companyID),
            Number(supplier.code),
            supplier.sapCode
        )
            .then((data) => {
                setPartDumpHeaderId(data.partDumpHeaderId);
                setValue("partNumbers", data.partNumbers);
            })
            .finally(() => {
                setCircularLoading(false);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supplier]);

    // useEffect que vai redirecionar para /NoPermission sempre que detectar uma ação sem permissão do usuário
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        !isAllowed && history.push('/NoPermission');
    }, [isAllowed]);

    const postFormValidation = async (data: any) => {
        var result = false;
        clearErrors();
        try {
            const schema = Yup.object().shape({
                supplier: Yup.object()
                    .nullable()
                    .required(tValidation("requiredField"))
                    .shape({
                        code: Yup.string().required(
                            tValidation("requiredField")
                        ),
                    }),
                partNumbers: Yup.array()
                    .nullable()
                    .required(tValidation("requiredField"))
                    .test(
                        "partNumbers",
                        ts("validationOneShippedPartNumberRequired"),
                        (value) => {
                            return (value as unknown as any[]).some(
                                (x: any) => x.shippingQuantity > 0
                            );
                        }
                    ),
            });

            await schema.validate(data, {
                abortEarly: false,
            });

            result = true;
        } catch (err) {
            var errors: string[] = [];

            if (err instanceof Yup.ValidationError) {
                err.inner.forEach((error) => {
                    if (
                        errors.filter((element) =>
                            element.includes(error.message)
                        ).length === 0
                    )
                        errors.push(error.message);

                    setError(error.path?.split(".")[0] as "supplier", {
                        message: error.message,
                    });
                });

                errors.forEach((element) => {
                    if (element === tValidation("requiredField"))
                        enqueueSnackbar(tGeneral("requiredFieldsValidation"), {
                            variant: "warning",
                        });
                    else enqueueSnackbar(element, { variant: "warning" });
                });
            }

            result = false;
        }

        return result;
    };

    const putFormValidation = async (data: any) => {
        var result = false;
        clearErrors();
        try {
            const schema = Yup.object().shape({
                partNumbers: Yup.array()
                    .nullable()
                    .required(tValidation("requiredField"))
                    .test(
                        "partNumbers",
                        ts("validationOneShippedPartNumberRequired"),
                        (value) => {
                            return (value as unknown as any[]).some(
                                (x: any) => x.shippingQuantity > 0
                            );
                        }
                    ),
                supplierObservation: Yup.string().nullable()
            });
            

            await schema.validate(data, {
                abortEarly: false,
            });

            result = true;
        } catch (err) {
            var errors: string[] = [];

            if (err instanceof Yup.ValidationError) {
                err.inner.forEach((error) => {
                    if (
                        errors.filter((element) =>
                            element.includes(error.message)
                        ).length === 0
                    )
                        errors.push(error.message);

                    setError(error.path?.split(".")[0] as "partNumbers", {
                        message: error.message,
                    });
                });

                errors.forEach((element) => {
                    if (element === tValidation("requiredField"))
                        enqueueSnackbar(tGeneral("requiredFieldsValidation"), {
                            variant: "warning",
                        });
                    else enqueueSnackbar(element, { variant: "warning" });
                });
            }

            result = false;
        }

        return result;
    };

    const onSubmit = async (submit: boolean = false) => {
        if (isValidate) 
            handleSubmitValidate(submit)
        else if(isEdit) 
            handleSubmitPut(submit)
        else
            handleSubmitPost(submit)
    };

    const handleSubmitPost = async (submit: boolean) => {
        let data: ShippingRequestPost = getValues();

        let validForm = await postFormValidation(data);

        data.partNumbers.forEach((x: any) => {
            x.id = 0;
            if (x.hasError) validForm = false;
        });

        if (validForm === false) return;

        setCircularLoading(true);

        data.plantId = +user.company.plantID;
        data.companyId = +user.company.companyID;
        data.requestDate = utilsDate.ConvertDateToStringYYYYMMDD(new Date());
        data.supplierId = data.supplier.code;
        data.supplierCorporateName = data.supplier.name;
        data.status = submit ? "INICIAR" : "RAS";
        data.partDumpHeaderId = partDumpHeaderId;
        data.partNumbers = data.partNumbers.filter(
            (x: any) => x.shippingQuantity > 0
        );

        ShippingRequestApi.create(data)
            .then(() => {
                enqueueSnackbar(tGeneral("returnSubmitEditFormSuccess"), {
                    variant: "success",
                });
                history.push("/ShippingRequest");
            })
            .finally(() => {
                setCircularLoading(false);
            });
    }

    const handleSubmitPut = async (submit: boolean) => {
        let data: ShippingRequestPut = getValues();

        let validForm = await putFormValidation(data);

        data.partNumbers.forEach((x: any) => {
            x.id = x.registeredId;
            if (x.hasError) validForm = false;
        });

        if (validForm === false) return;

        setCircularLoading(true);

        data.id = shippingRequest.id;
        data.companyId = +user.company.companyID;
        data.plantId = +user.company.plantID;
        data.requestDate = utilsDate.ConvertDateToStringYYYYMMDD(new Date());
        data.supplierId = shippingRequest.supplierId;
        data.supplierCorporateName = shippingRequest.supplierCorporateName;
        data.status = shippingRequest.status;
        data.partDumpHeaderId = partDumpHeaderId;
        data.partNumbers = data.partNumbers.filter(
            (x: any) => x.shippingQuantity > 0
        );

        ShippingRequestApi.put(data)
            .then(() => {
                enqueueSnackbar(tGeneral("returnSubmitEditFormSuccess"), {
                    variant: "success",
                });
                history.push("/ShippingRequest");
            })
            .finally(() => {
                setCircularLoading(false);
            });
    }

    const handleSubmitValidate = async (submit: boolean = false) => {
        let data: ShippingRequestPut = getValues();

        let validForm = await putFormValidation(data);

        data.partNumbers.forEach((x: any) => {
            x.id = x.registeredId;
            if (x.hasError) validForm = false;
        });

        if (validForm === false) return;

        setCircularLoading(true);

        data.id = shippingRequest.id;
        data.companyId = +user.company.companyID;
        data.plantId = +user.company.plantID;
        data.requestDate = utilsDate.ConvertDateToStringYYYYMMDD(new Date());
        data.supplierId = shippingRequest.supplierId;
        data.supplierCorporateName = shippingRequest.supplierCorporateName;
        data.status = shippingRequest.status;
        data.partDumpHeaderId = partDumpHeaderId;
        data.partNumbers = data.partNumbers.filter(
            (x: any) => x.shippingQuantity > 0
        );

        if (isApproved || submit)
            handleApproveValidation(data);
        else
            handleRejectValidation(data);

    }

    function handleApproveValidation(data: any) {
        ShippingRequestApi.approve(data)
        .then(() => {
            enqueueSnackbar(ts("returnSubmitApproveFormSuccess"), {
                variant: "success",
            });
            history.push("/ShippingRequest");
        })
        .finally(() => {
            setCircularLoading(false);
        });
    }

    function handleRejectValidation(data: any) {
        ShippingRequestApi.reject(data)
        .then(() => {
            enqueueSnackbar(ts("returnSubmitRejectFormSuccess"), {
                variant: "success",
            });
            history.push("/ShippingRequest");
        })
        .finally(() => {
            setCircularLoading(false);
        });
    }

    const handleClickOpen = (approved: boolean) => {
        setIsApproved(approved);
        setOpenedPainel(true);
    };

    const handleClose = () => {
        setOpenedPainel(false);
    };

    return (
        <Container maxWidth={false} component="main">
            <React.Fragment>
                <Dialog
                    open={openedPainel}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    PaperComponent={PaperComponent}
                    onBackdropClick={() => setOpenedPainel(false)}
                    >

                    <DialogTitle classes={{ root: classes.dialogTitle }}
                        id="draggable-dialog-title"
                        onClose={() => setOpenedPainel(false)}>
                        { ts(isApproved ? "approve" : "reject") }

                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                        { ts(isApproved ? "approveContentBox" : "rejectContentBox") }
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>

                        <ButtonUI
                            variant="outlined"
                            onClick={handleClose}
                        >
                            { tGeneral("cancel") }
                        </ButtonUI>

                        <ButtonUI
                            variant="contained"
                            color="primary"
                            type="submit"
                            startIcon={<CheckIcon />}
                            onClick={() => onSubmit()}
                        >
                            { tGeneral("confirm") }
                        </ButtonUI>

                    </DialogActions>

                </Dialog>
            </React.Fragment>

            <PageTitle title={title} />
            <div className={classes.rootContent}>
                <div className={classes.paper}>
                    <form>

                        {rejectedWarning &&
                        (<Grid container spacing={1}>
                            <Grid item xs={7}><Alert severity="warning" onClose={() => { setRejectedWarning(false); }}>{ts('validation.rejectedReason')}</Alert></Grid>
                        </Grid>)}

                        <Grid container spacing={1}>
                            <Grid item xs={3}>
                                {isEdit || isValidate ? (
                                    <TextField
                                        label={ts("supplier")}
                                        name="supplierCorporateName"
                                        control={control}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{   
                                            readOnly: true,
                                            classes: {
                                                input: classes.inputBlocked,
                                            },
                                        }}
                                    />
                                    ) : (
                                    <SelectSupplier
                                        label={ts("supplier")}
                                        name={"supplier"}
                                        control={control}
                                        style={{ width: "100%" }}
                                        multiple={false}
                                        setValue={setValue}
                                        loadUserSupplier={true}
                                        onChange={setSupplier}
                                    />
                                    )
                                }
                            </Grid>
                            <Grid item xs={1}>
                                <TextField
                                    label={ts("requestDate")}
                                    name="requestDate"
                                    control={control}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        readOnly: true,
                                        classes: {
                                            input: classes.inputBlocked,
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    label={ts("status")}
                                    name="statusDescription"
                                    control={control}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        readOnly: true,
                                        classes: {
                                            input: classes.inputBlocked,
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={1}>
                            <Grid item xs={7}>
                                <TextField
                                    label={ts("supplierObservation")}
                                    name="supplierObservation"
                                    control={control}
                                    disable={isValidate}
                                    variant={"outlined"}
                                    multiline={true}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        classes: { input: classes.rootTextBox },
                                    }}
                                />
                            </Grid>
                        </Grid>
                        {
                            isValidate &&
                            <Grid container spacing={1}>
                                <Grid item xs={7}>
                                    <TextField
                                        label={ts("programmerObservation")}
                                        name="programmerObservation"
                                        control={control}
                                        variant={"outlined"}
                                        disable={isEdit || isCreate}
                                        multiline={true}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            classes: { input: classes.rootTextBox },
                                        }}
                                        />
                                </Grid>
                            </Grid>
                        }
                    </form>

                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            {
                                <PartNumberList
                                    control={control}
                                    name={"partNumbers"}
                                    allowAdd={!isValidate}
                                />
                            }
                        </Grid>
                    </Grid>

                    <Grid container spacing={1}>
                        <Grid
                            item
                            xs={12}
                            style={{
                                padding: "0px",
                                textAlign: "left",
                                marginTop: "30px",
                                marginLeft: "5px",
                            }}
                        >
                            <Backdrop
                                style={{ zIndex: 9999 }}
                                open={circularLoading}
                            >
                                <CircularProgress color="inherit" />
                            </Backdrop>
                            
                            {
                                isValidate ? (
                                    <>
                                        <ButtonUI
                                            onClick={() => handleClickOpen(true)}
                                            color="primary"
                                            startIcon={<CheckCircleIcon/>}
                                        >
                                            {tGeneral("approve")}
                                        </ButtonUI>

                                        <ButtonUI
                                            onClick={() => handleClickOpen(false)}
                                            color="primary"
                                            startIcon={<HighlightOffIcon/>}
                                        >
                                            {tGeneral("disapprove")}
                                        </ButtonUI>

                                        <ButtonUI
                                            variant="outlined"
                                            color="secondary"
                                            onClick={() =>
                                                history.replace("/ShippingRequest")
                                            }
                                            startIcon={<ArrowBack />}
                                        >
                                            {tGeneral("back")}
                                        </ButtonUI>
                                    </>
                                ) : (
                                        <>
                                <ButtonUI
                                variant="outlined"
                                color="secondary"
                                onClick={() =>
                                    history.replace("/ShippingRequest")
                                }
                                startIcon={<ArrowBack />}
                            >
                                {tGeneral("back")}
                            
                                </ButtonUI>
                                        <ButtonUI
                                            onClick={() => onSubmit(false)}
                                            color="primary"
                                            startIcon={isEdit ? <SaveIcon /> : <AddIcon />}
                                        >
                                            {tGeneral(isEdit? "save" : "add")}
                                        </ButtonUI>


                                        <ButtonUI
                                            onClick={() => onSubmit(true)}
                                            color="primary"
                                        >
                                            {tGeneral("submitForApproval")}
                                        </ButtonUI>
                                        </>
                                )
                            }
        
                        </Grid>
                    </Grid>
                </div>
            </div>
        </Container>
    );
};
export default Submit;
